// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { forwardRef, useId } from 'react';
import './Checkbox.css';

interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: string;
  size?: 'md' | 'sm';
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, size = 'md', className = '', id: idProp, disabled, ...rest }, ref) => {
    const autoId = useId();
    const id = idProp ?? autoId;

    const classes = [
      'fin-checkbox',
      `fin-checkbox--${size}`,
      disabled && 'fin-checkbox--disabled',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <label className={classes} htmlFor={id}>
        <input
          ref={ref}
          id={id}
          type="checkbox"
          className="fin-checkbox__input"
          disabled={disabled}
          {...rest}
        />
        <span className="fin-checkbox__control">
          <svg
            className="fin-checkbox__check"
            viewBox="0 0 12 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 5L4.5 8.5L11 1.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        {label && <span className="fin-checkbox__label">{label}</span>}
      </label>
    );
  },
);

Checkbox.displayName = 'Checkbox';
